import { COLORS2 } from '@/Client/Styles';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { createTheme } from '@mui/material/styles';

export const materialTheme = createTheme({
  typography: {
    htmlFontSize: 10,
    fontFamily: `"Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;`,
  },
  components: {
    MuiDrawer: {
      styleOverrides: {
        root: {
          // defaults to 1200; mapbox's autocomplete is set to 1000
          zIndex: 999,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          backgroundColor: `${COLORS2.namedColors.white}`,
          borderRadius: '10px',
        },
      },
    },
    MuiInputLabel: {
      defaultProps: {
        shrink: true,
      },
      styleOverrides: {
        root: {
          backgroundColor: 'white',
          left: '-4px',
          padding: '0 4px',
          position: 'absolute',
          color: COLORS2.namedColors.gray,
          '&.Mui-focused': {
            color: COLORS2.namedColors.darkYellow,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: '10px',
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        InputLabelProps: {
          shrink: true,
        },
      },
      styleOverrides: {
        root: {
          borderRadius: '10px',
          '& .MuiInputBase-root': {
            borderRadius: '10px',
          },
          input: {
            padding: '20px 20px 16px 20px',
            borderRadius: '10px',
          },
          ' label.Mui-disabled': {
            color: `${COLORS2.namedColors.darkGray}`,
            fontWeight: 600,
          },
          ' .MuiInputBase-input.Mui-disabled': {
            backgroundColor: `${COLORS2.namedColors.lightGray}`,
          },
          ' .Mui-disabled fieldset': {
            border: 'none',
          },
          // input text color, error state
          '& .MuiInputLabel-root.Mui-error': {
            color: COLORS2.namedColors.red,
          },
          // input border, error state
          '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: `${COLORS2.namedColors.red} !important`,
          },
          // input inset label, error state
          '& .MuiFormLabel-root.Mui-error.Mui-focused': {
            color: `${COLORS2.namedColors.red} !important`,
          },
          // input inset label
          '& .MuiFormLabel-root.Mui-focused': {
            color: COLORS2.namedColors.darkYellow,
          },
          // input active border
          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            border: `solid 1px ${COLORS2.namedColors.darkYellow}`,
          },
          // input text color & override arbitrary fixed height
          '& .MuiInputBase-input': {
            color: COLORS2.namedColors.darkYellow,
            height: 'auto',
          },
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: COLORS2.namedColors.gray,
          padding: 0,
          '&:hover': {
            background: 'none',
          },
          '&:hover span svg:last-child': {
            color: COLORS2.namedColors.gray,
            transform: 'scale(1)',
          },
          '&.Mui-checked': {
            backgroundColor: COLORS2.namedColors.darkYellow,
            color: COLORS2.namedColors.darkYellow,
            padding: 0,
          },
          'svg:last-child': {
            color: 'white',
          },
          '&.Mui-disabled': {
            color: COLORS2.namedColors.gray,
            backgroundColor: COLORS2.namedColors.gray,
          },
          '&.Mui-disabled svg:last-child': {
            transform: 'scale(1)',
          },
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          '& .MuiRadioGroup-root .MuiFormControlLabel-root': {
            gap: '9px',
            padding: '9px',
          },
          ' .Mui-focused': {
            color: `${COLORS2.namedColors.darkYellow} !important`,
          },
          '& .MuiPickersInputBase-root': {
            borderRadius: '10px',
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: pxToRem(14),
          width: '100%',
          '&:hover': {
            backgroundColor: COLORS2.namedColors.lightYellow,
          },
          '&.Mui-focusVisible': {
            backgroundColor: COLORS2.namedColors.lightYellow,
          },
          '&.Mui-selected.Mui-focusVisible': {
            backgroundColor: COLORS2.namedColors.lightYellow,
            fontWeight: 600,
          },
          '&.Mui-selected.Mui-focusVisible:hover': {
            backgroundColor: COLORS2.namedColors.lightYellow,
          },
          '&.Mui-selected:hover, &.Mui-selected:hover': {
            backgroundColor: COLORS2.namedColors.lightYellow,
          },
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          '& > .Mui-selected': {
            backgroundColor: `${COLORS2.namedColors.white} !important`,
          },
          '& > .Mui-selected:hover': {
            backgroundColor: COLORS2.namedColors.lightYellow,
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: '10px',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          alignItems: 'center',
          borderRadius: '8px',
          color: COLORS2.namedColors.white,
          '&.MuiAlert-standardSuccess': {
            background: COLORS2.namedColors.green,
          },
          '&.MuiAlert-standardInfo': {
            background: COLORS2.namedColors.darkYellow,
          },
          '&.MuiAlert-standardError': {
            background: COLORS2.namedColors.red,
          },
          '> .MuiAlert-icon': {
            alignSelf: 'start',
            color: COLORS2.namedColors.white,
          },
          ' .MuiAlert-action': {
            alignSelf: 'start',
            paddingTop: 0,
          },
          a: {
            color: `${COLORS2.namedColors.white}`,
          },
        },
      },
    },
    MuiAlertTitle: {
      styleOverrides: {
        root: {
          fontSize: pxToRem(14),
          fontWeight: '600',
          lineHeight: 1,
          margin: 0,
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          textTransform: 'capitalize',
        },
      },
    },
  },
});
